import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import style from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { associationTypes, Owner, Panel, Site } from '@interfaces-clean/observations.interface';
import { isPanelScatterPlot } from '@utils-clean/panel-helpers';
import { useBoolean, useTranslation } from '@hooks/index';
import {
    GET_CM2_PANELS,
    GET_DATA_VIEW_ACTIVE_CONFIGURATION_NAMES,
    GET_STUDY_DASHBOARD_CONFIG_PANELS,
} from '@queries/central-monitoring';
import {
    getCm2Panels_OpraQueries as getCm2PanelsResponse,
    getCm2PanelsVariables,
    getStudyDashboardConfigPanelsForStudy,
    getStudyDashboardConfigPanelsForStudyVariables,
    datasetDataViewConfigurations_OpraQueries_dataViewConfigurations_DataViewConfiguration as DataViewConfiguration,
    datasetDataViewConfigurations,
    datasetDataViewConfigurationsVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import { useGetDatasetSites } from '@apis/bff-api/central-monitoring';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

interface AddObservationProps {
    onClose: () => void;
    onSubmit: (data: FormInputs) => void;
    orgKey: string;
    studyRef: string;
}

export type FormInputs = {
    datasetOpenedOn: CM2Dataset | null;
    associationType: DataViewConfiguration | null;
    panel: Panel | null;
    site: Site | null;
    observationDate: Date | null;
    owner: Owner | null;
    observation: string;
    rootCause: string | null;
    comments: string | null;
    closedDate: Date | null;
    isScatterPlot: boolean;
};

const COMPONENT_NAME = 'add-site-observation-dialog';

const AddSiteObservationDialog = ({ onClose, onSubmit, orgKey, studyRef }: AddObservationProps) => {
    const { t } = useTranslation('cm');

    // State
    const isDisabled = useBoolean(true);
    const isPanelsDisabled = useBoolean(true);
    const [isPanelRequired, setIsPanelRequired] = useState(false);
    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string } | null)[] | undefined
    >([]);
    const [sites, setSites] = useState<Site[]>([]);
    const [dataViewConfig, setDataViewConfig] = useState<(DataViewConfiguration | null)[] | null | undefined>(
        associationTypes,
    );
    const [panelItems, setPanelItems] = useState<Panel[]>();

    // Load Datasets
    const { data: datasetOpenedOn, isLoading: isLoadingDatasets } = DatasetApi.useGetActive(studyRef);

    const [studySiteHubConfigPanelItems, setStudySiteHubConfigPanelItems] = useState<string[]>();
    const cm2DataSets = datasetOpenedOn?.filter((x) => x.active === true)?.filter(Boolean) ?? [];
    const sortedCM2DataSets = cm2DataSets.map(formatDatasetDates).sort(sortDatasetByDatesDesc) ?? [];

    // Load all panels for a dataset with latest titles
    const [loadPanels, { data: getPanelsRawData, loading: loadingPanels }] = useLazyQuery<
        getCm2PanelsResponse,
        getCm2PanelsVariables
    >(GET_CM2_PANELS);

    // Load DataView Configs
    const [loadConfigs, { data: dataViewConfigs, loading: isLoadingDataConfigs }] = useLazyQuery<
        datasetDataViewConfigurations,
        datasetDataViewConfigurationsVariables
    >(GET_DATA_VIEW_ACTIVE_CONFIGURATION_NAMES);

    // Study/Site Hub Config Panels
    const [getStudyDashboardConfigPanels, { data: getStudyDashboardConfigPanelsRawData }] = useLazyQuery<
        getStudyDashboardConfigPanelsForStudy,
        getStudyDashboardConfigPanelsForStudyVariables
    >(GET_STUDY_DASHBOARD_CONFIG_PANELS);

    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter((user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember => !!user)
                .map((user) => ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    ref: user.id as string,
                    groupedBy: user.active ? 'Active' : 'Inactive',
                    disabled: !user.active,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.fullName.localeCompare(b.fullName);
                });
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const {
        control,
        handleSubmit,
        watch,
        resetField,
        setValue,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            datasetOpenedOn: null,
            associationType: null,
            panel: null,
            site: null,
            owner: null,
            observationDate: null,
            observation: '',
            rootCause: '',
            comments: '',
            closedDate: null,
            isScatterPlot: false,
        },
    });
    const [watchDatasetOpenedOn, watchAssociationType, watchPanelType] = watch([
        'datasetOpenedOn',
        'associationType',
        'panel',
    ]);
    const { data: dataSites, isLoading } = useGetDatasetSites(studyRef, watchDatasetOpenedOn?.id);

    useEffect(() => {
        let isScatter = false;
        if (watchPanelType) {
            const panel = getPanelsRawData?.cm2panels?.find((x) => x?.id === watchPanelType.reference);
            isScatter = isPanelScatterPlot(panel);
        }
        setValue('isScatterPlot', isScatter);
    }, [watchPanelType]);

    useEffect(() => {
        if (getPanelsRawData?.cm2panels) {
            const panels = getPanelsRawData?.cm2panels
                ?.filter((item) => item != null && item.active)
                .map((item) => ({
                    reference: item?.id as string,
                    name: item?.name ?? '[Name Missing]',
                    plotTitle: item?.graph?.plotTitle ?? '[Title Missing]',
                }))
                .sort((a, b) => a.plotTitle.localeCompare(b.plotTitle));

            setPanelItems(panels);
        }
    }, [getPanelsRawData]);

    useEffect(() => {
        const isDataViewConfig =
            watchAssociationType != null && !associationTypes.some((x) => x.id === watchAssociationType.id);
        setIsPanelRequired(isDataViewConfig);

        // If it is a data view we then need to filter the panels by the data views panelIds
        // for that timestamp and check that the mainPanelId is included
        if (isDataViewConfig) {
            const panels = getPanelsRawData?.cm2panels
                ?.filter(
                    (item) =>
                        item != null &&
                        item.active &&
                        (watchAssociationType?.panelIds?.includes(item.id as string) ||
                            watchAssociationType?.mainPanelId === (item.id as string)),
                )
                .map((item) => ({
                    reference: item?.id as string,
                    name: item?.name ?? '[Name Missing]',
                    plotTitle: item?.graph?.plotTitle ?? '[Title Missing]',
                }))
                .sort((a, b) => a.plotTitle.localeCompare(b.plotTitle));

            setPanelItems(panels);
        } else {
            // If it isn't a data view and filtering has occurred we set the panelItems back to the
            // original list of panels
            const panels = getPanelsRawData?.cm2panels
                ?.filter(
                    (item) => item != null && item.active && studySiteHubConfigPanelItems?.includes(item.id as string),
                )
                .map((item) => ({
                    reference: item?.id as string,
                    name: item?.name ?? '[Name Missing]',
                    plotTitle: item?.graph?.plotTitle ?? '[Title Missing]',
                }))
                .sort((a, b) => a.plotTitle.localeCompare(b.plotTitle));

            setPanelItems(panels);
        }
        if (watchAssociationType && watchAssociationType?.id !== null) {
            isPanelsDisabled.setFalse();
        } else {
            isPanelsDisabled.setTrue();
        }
        resetField('panel');
    }, [watchAssociationType]);

    useEffect(() => {
        if (getStudyDashboardConfigPanelsRawData?.getStudyDashboardConfigPanelsForStudy?.panelRefs) {
            // @ts-ignore
            const allPanelIds: string[] | undefined =
                getStudyDashboardConfigPanelsRawData.getStudyDashboardConfigPanelsForStudy.panelRefs;
            if (allPanelIds && allPanelIds.length > 0) {
                setStudySiteHubConfigPanelItems(allPanelIds);
            }
        }
    }, [getStudyDashboardConfigPanelsRawData]);

    useEffect(() => {
        setDataViewConfig([
            ...associationTypes,
            ...(
                dataViewConfigs?.dataViewConfigurations?.filter(
                    (x): x is DataViewConfiguration => x != null && x?.isActive === true,
                ) ?? []
            ).sort((a, b) => a.name!.localeCompare(b.name!)),
        ]);
    }, [dataViewConfigs]);

    useEffect(() => {
        if (dataSites) {
            const mappedSites: Site[] = dataSites
                ?.map((site) => ({
                    id: site.id,
                    name: site.siteName,
                }))
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
            setSites(mappedSites);
        }
    }, [dataSites]);

    useEffect(() => {
        resetField('panel');
        resetField('site');
        resetField('associationType');

        if (watchDatasetOpenedOn) {
            isDisabled.setFalse();
            loadConfigs({ variables: { studyId: studyRef, timestamp: watchDatasetOpenedOn.created } }).catch((err) =>
                window.console.error(err),
            );
            getStudyDashboardConfigPanels({
                variables: { studyId: studyRef, timestamp: watchDatasetOpenedOn.created },
            }).catch((err) => window.console.error(err));
            loadPanels({
                variables: { organisationId: orgKey, studyId: studyRef, timestamp: watchDatasetOpenedOn?.created },
            }).catch((err) => window.console.error(err));
        } else {
            isDisabled.setTrue();
        }
    }, [watchDatasetOpenedOn]);

    const dialogTitle = `${t('common:add')} ${t('observationDialog.siteTitle')}`;
    const primaryBtnLabel = t('common:add');

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={630}
            initialWidth={522}
        >
            <form id="add-edit-observation" className="k-w-full k-h-full">
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="datasetOpenedOn"
                            data-testid="field-select-datasetOpenedOn"
                            data={sortedCM2DataSets}
                            required
                            textField="dataCutDate"
                            dataItemKey="id"
                            label={t('observationDialog.datasetOpenedOn')}
                            loading={isLoadingDatasets}
                            disabled={isLoadingDatasets}
                            filterable
                            allowClear
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="associationType"
                            data-testid="field-select-associationType"
                            data={dataViewConfig ?? []}
                            textField="name"
                            required
                            disabled={isDisabled.value || isLoadingDataConfigs}
                            label={t('observationDialog.association')}
                            loading={isLoadingDataConfigs}
                            filterable
                            allowClear
                            dataItemKey="id"
                            readOnly={isSubmitting}
                        />
                    </div>
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                    <BasicDropDownController
                        control={control}
                        name="panel"
                        data-testid="field-select-panel"
                        data={panelItems ?? []}
                        required={isPanelRequired}
                        textField="plotTitle"
                        disabled={isPanelsDisabled.value || loadingPanels}
                        label={`${t('observationDialog.panel')}${isPanelRequired ? '*' : ''}`}
                        loading={loadingPanels}
                        filterable
                        allowClear
                        dataItemKey="reference"
                        readOnly={isSubmitting}
                    />
                </div>

                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="site"
                            data-testid="field-select-site"
                            data={sites ?? []}
                            textField="name"
                            disabled={isDisabled.value || isLoading}
                            label={t('observationDialog.site')}
                            required
                            loading={isLoading}
                            dataItemKey="id"
                            filterable
                            allowClear
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <DatePickerController
                            control={control}
                            name="observationDate"
                            data-testid="datepicker-observationDate"
                            label={t('observationDialog.observationDate')}
                            fillMode="solid"
                            required
                            readonly={isSubmitting}
                        />
                    </div>
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                    <BasicDropDownController
                        control={control}
                        name="owner"
                        data-testid="field-select-owner"
                        data={studyMembers ?? []}
                        textField="fullName"
                        groupField="groupedBy"
                        dataItemKey="ref"
                        label={t('observationDialog.owner')}
                        loading={loadingStudyMembers}
                        disabled={loadingStudyMembers}
                        filterable
                        allowClear
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="observation"
                        required
                        data-testid="field-select-observation"
                        label={t('observationDialog.observation')}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        maxLength={3000}
                        className={style.textAreaOverrides}
                        control={control}
                        name="rootCause"
                        data-testid="field-select-rootCause"
                        label={t('observationDialog.rootCause')}
                        rows={4}
                        fillMode="outline"
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-pb-md k-input-md">
                    <TextAreaDropdownController
                        control={control}
                        className={style.textAreaOverrides}
                        name="comments"
                        data-testid="field-select-comments"
                        label={t('observationDialog.comments')}
                        rows={4}
                        fillMode="outline"
                        maxLength={3000}
                        readOnly={isSubmitting}
                    />
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-start k-pb-md">
                    <div className="k-flex-row !k-basis-1/2">
                        <DatePickerController
                            control={control}
                            name="closedDate"
                            data-testid="datepicker-closedDate"
                            label={t('observationDialog.closedDate')}
                            fillMode="solid"
                            readonly={isSubmitting}
                        />
                    </div>
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddSiteObservationDialog;
